import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ShoppingBag,
  User,
  CaretDown,
  List,
  X,
  SignOut,
  ClipboardText
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import CartModal from '../modals/CartModal';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/stores', label: 'Stores' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' }
];

const Header = () => {
  const { user, logout } = useAuth();
  const { cartItems } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const toggleCart = () => setIsCartOpen((v) => !v);
  const toggleMobileMenu = () => setIsMobileMenuOpen((v) => !v);
  const toggleUserMenu = () => setIsUserMenuOpen((v) => !v);

  const handleOrderNow = () => navigate('/stores');

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Failed to logout', err);
    }
  };

  const cartCount = cartItems.length;

  return (
    <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="h-9 w-9 rounded-xl bg-amber-600 text-white flex items-center justify-center text-base font-bold shadow-sm">
              Z
            </span>
            <span className="text-xl font-bold text-gray-900 tracking-tight">Zimzo</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? 'bg-amber-100 text-amber-700'
                    : 'text-gray-600 hover:text-amber-600 hover:bg-amber-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={toggleCart}
              className="relative p-2.5 rounded-full text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag size={22} weight="bold" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-xs font-bold text-white bg-amber-600 rounded-full">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              onClick={handleOrderNow}
              className="bg-amber-600 text-white font-medium text-sm py-2.5 px-5 rounded-full hover:bg-amber-700 transition-colors shadow-sm active:scale-[0.98]"
            >
              Order Now
            </button>

            <div className="relative">
              <button
                onClick={toggleUserMenu}
                className="flex items-center gap-1 p-2.5 rounded-full text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                aria-label="User menu"
              >
                <User size={22} weight="bold" />
                <CaretDown size={14} weight="bold" />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-md border border-gray-100 py-1.5 z-50 overflow-hidden">
                  {user ? (
                    <>
                      <Link to="/profile" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50">My Profile</Link>
                      <Link to="/order-history" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50">View Orders</Link>
                      <button onClick={handleLogout} className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50">Sign Out</button>
                    </>
                  ) : (
                    <Link to="/profile" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50">Sign In</Link>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex md:hidden items-center gap-1">
            <button onClick={toggleCart} className="flex items-center justify-center w-11 h-11 text-gray-700" aria-label="Cart">
              <span className="relative inline-flex">
                <ShoppingBag size={22} weight="bold" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-xs font-bold text-white bg-amber-600 rounded-full">
                    {cartCount}
                  </span>
                )}
              </span>
            </button>
            <button onClick={toggleMobileMenu} className="flex items-center justify-center w-11 h-11 text-gray-700" aria-label="Menu">
              {isMobileMenuOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden mt-3 pt-3 pb-3 border-t border-gray-100">
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="font-medium text-gray-700 hover:text-amber-600 transition-colors px-3 py-2 rounded-xl hover:bg-amber-50"
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-2 mt-2 border-t border-gray-100 flex flex-col gap-1">
                {user ? (
                  <>
                    <Link to="/profile" className="flex items-center gap-2 font-medium text-gray-700 hover:text-amber-600 transition-colors px-3 py-2 rounded-xl hover:bg-amber-50">
                      <User size={18} weight="bold" /> My Profile
                    </Link>
                    <Link to="/order-history" className="flex items-center gap-2 font-medium text-gray-700 hover:text-amber-600 transition-colors px-3 py-2 rounded-xl hover:bg-amber-50">
                      <ClipboardText size={18} weight="bold" /> View Orders
                    </Link>
                    <button onClick={handleLogout} className="flex items-center gap-2 w-full font-medium text-gray-700 hover:text-amber-600 transition-colors px-3 py-2 rounded-xl hover:bg-amber-50">
                      <SignOut size={18} weight="bold" /> Sign Out
                    </button>
                  </>
                ) : (
                  <Link to="/profile" className="flex items-center gap-2 font-medium text-gray-700 hover:text-amber-600 transition-colors px-3 py-2 rounded-xl hover:bg-amber-50">
                    <User size={18} weight="bold" /> Sign In
                  </Link>
                )}
              </div>

              <button
                onClick={handleOrderNow}
                className="bg-amber-600 text-white font-medium py-3 px-4 rounded-full hover:bg-amber-700 transition-colors w-full text-center mt-3 shadow-sm"
              >
                Order Now
              </button>
            </nav>
          </div>
        )}
      </div>

      {isCartOpen && <CartModal onClose={toggleCart} />}
    </header>
  );
};

export default Header;
